import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Trash2, Undo2 } from 'lucide-react'
import { Chip } from '@/components/original/games/baccarat/components/Chip'
import { ROULETTE_CHIP_RAW_VALUES, ROULETTE_CHIP_RATIO } from '@/config/original'
import { cn } from '@/lib/utils'
import type { RouletteChipStackItem, RoulettePlacedChip } from '@/types/roulette'

interface RouletteBettingBoardProps {
	pendingChips: RoulettePlacedChip[]
	hoveredPlace: string | null
	winningNumber: number
	isResultVisible: boolean
	isDisabled: boolean
	onHoverPlace: (place: string | null) => void
	onPlaceBet: (place: string) => void
	onUndo: () => void
	onClear: () => void
}

const PLACED_CHIP_SIZE = 22
const PLACED_CHIP_STACK_OFFSET = 3
const PLACED_CHIP_MAX_VISIBLE = 6

function getDesktopBoardNumber(position: number, dozenIndex: number): number {
	return (position + 1) * 3 - 13 * Math.floor(position / 4) + 12 * dozenIndex
}

function getBoardCellColorIndex(number: number): 0 | 1 {
	const oddBlock = Math.ceil(number / 9) % 2 === 0
	let color: 0 | 1 = oddBlock ? (number % 2 ? 0 : 1) : number % 2 ? 1 : 0
	if (number === 28 || number === 10) {
		color = 0
	}
	return color
}

function isNumberInColumn(number: number, columnIndex: number): boolean {
	if (number <= 0) return false
	if (columnIndex === 0) return number % 3 === 0
	if (columnIndex === 1) return number % 3 === 2
	return number % 3 === 1
}

function buildChipStack(rawAmount: number): RouletteChipStackItem[] {
	for (let index = ROULETTE_CHIP_RAW_VALUES.length - 1; index >= 0; index -= 1) {
		const chipValue = ROULETTE_CHIP_RAW_VALUES[index]
		if (rawAmount >= chipValue) {
			return [{ chipIndex: index, count: Math.floor(rawAmount / chipValue) }]
		}
	}
	return []
}

function getRawAmountForPlace(chips: RoulettePlacedChip[], place: string): number {
	const real = chips.filter((chip) => chip.place === place).reduce((total, chip) => total + chip.amount, 0)
	return Math.round(real * ROULETTE_CHIP_RATIO)
}

function isPlaceHighlighted(place: string, hoveredPlace: string | null, numberOnCell?: number): boolean {
	if (hoveredPlace === null) return false
	if (hoveredPlace === place) return true
	if (typeof numberOnCell !== 'number') return false

	if (/^\d+$/.test(hoveredPlace)) return Number(hoveredPlace) === numberOnCell

	if (hoveredPlace.includes('_to_')) {
		const [startText, endText] = hoveredPlace.split('_to_')
		return numberOnCell >= Number(startText) && numberOnCell <= Number(endText)
	}

	if (hoveredPlace === 'Red') return getBoardCellColorIndex(numberOnCell) === 1
	if (hoveredPlace === 'Black') return getBoardCellColorIndex(numberOnCell) === 0
	if (hoveredPlace === 'Odd') return numberOnCell > 0 && numberOnCell % 2 === 1
	if (hoveredPlace === 'Even') return numberOnCell > 0 && numberOnCell % 2 === 0

	if (hoveredPlace.startsWith('2:1:')) {
		return isNumberInColumn(numberOnCell, Number(hoveredPlace.split(':')[2]))
	}

	return false
}

function PlacedChips({ place, pendingChips }: { place: string; pendingChips: RoulettePlacedChip[] }) {
	const rawAmount = getRawAmountForPlace(pendingChips, place)
	if (rawAmount <= 0) {
		return null
	}

	const stacks = buildChipStack(rawAmount)
	const chips = stacks.flatMap((stack, stackIndex) => {
		const visibleCount = Math.min(stack.count, PLACED_CHIP_MAX_VISIBLE)
		return Array.from({ length: visibleCount }, (_, chipIndex) => ({
			chipIndex: stack.chipIndex,
			stackIndex,
			chipIndexInStack: chipIndex,
		}))
	})

	return (
		<div className='relative z-10'>
			{chips.map((chip, stackPosition) => (
				<div
					key={`${chip.chipIndex}-${chip.chipIndexInStack}-${chip.stackIndex}`}
					className='absolute -translate-x-1/2 -translate-y-1/2'
					style={{
						top: `${-stackPosition * PLACED_CHIP_STACK_OFFSET}px`,
						zIndex: stackPosition + 1,
					}}
				>
					<Chip chipIndex={chip.chipIndex} size={PLACED_CHIP_SIZE} />
				</div>
			))}
		</div>
	)
}

function ActionButton({ label, icon: Icon, disabled, onClick, iconClassName }: { label: string; icon: LucideIcon; disabled: boolean; onClick: () => void; iconClassName: string }) {
	return (
		<button
			type='button'
			disabled={disabled}
			className='inline-flex shrink-0 items-center justify-center gap-0.5 whitespace-nowrap rounded border border-white/20 bg-black/40 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow-[0_4px_14px_rgba(0,0,0,0.25)] backdrop-blur-md transition hover:border-white/35 hover:bg-black/55 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40 md:gap-2 md:rounded-xl md:px-3.5 md:py-2 md:text-sm'
			onClick={onClick}
		>
			<Icon className={iconClassName} aria-hidden />
			{label}
		</button>
	)
}

function BoardCell({
	place,
	className,
	disabled,
	pendingChips,
	onHoverPlace,
	onPlaceBet,
	children,
}: {
	place: string
	className?: string
	disabled: boolean
	pendingChips: RoulettePlacedChip[]
	onHoverPlace: (place: string | null) => void
	onPlaceBet: (place: string) => void
	children: ReactNode
}) {
	return (
		<div
			role='button'
			tabIndex={disabled ? -1 : 0}
			className={cn(
				'relative flex cursor-pointer select-none items-center justify-center rounded border border-[#ffffff3d] font-bold text-white',
				className,
				disabled && 'pointer-events-none',
			)}
			onMouseEnter={() => onHoverPlace(place)}
			onMouseLeave={() => onHoverPlace(null)}
			onClick={() => onPlaceBet(place)}
		>
			{children}
			<div className='absolute left-1/2 top-1/2'>
				<PlacedChips place={place} pendingChips={pendingChips} />
			</div>
		</div>
	)
}

export function RouletteBettingBoard({
	pendingChips,
	hoveredPlace,
	winningNumber,
	isResultVisible,
	isDisabled,
	onHoverPlace,
	onPlaceBet,
	onUndo,
	onClear,
}: RouletteBettingBoardProps) {
	const actionsDisabled = isDisabled || pendingChips.length === 0

	const renderNumberCell = (position: number, dozenIndex: number) => {
		const number = getDesktopBoardNumber(position, dozenIndex)
		const place = String(number)
		const colorIndex = getBoardCellColorIndex(number)
		const highlighted = isPlaceHighlighted(place, hoveredPlace, number) || (isResultVisible && number === winningNumber)
		const tone = colorIndex === 0 ? (highlighted ? 'bg-[#709cb9]' : 'bg-[#2f4553]') : highlighted ? 'bg-[#fa617b]' : 'bg-[#fe2247]'

		return (
			<BoardCell
				key={`${dozenIndex}-${position}`}
				place={place}
				className={cn('aspect-[6/5] text-[9px] md:aspect-square md:text-sm', tone)}
				disabled={isDisabled}
				pendingChips={pendingChips}
				onHoverPlace={onHoverPlace}
				onPlaceBet={onPlaceBet}
			>
				{number}
			</BoardCell>
		)
	}

	const renderOutsideBets = (dozenIndex: number) => {
		const startNumber = 1 + 12 * dozenIndex
		const endNumber = 12 + 12 * dozenIndex
		const leftId = dozenIndex === 0 ? '1_to_18' : dozenIndex === 1 ? 'Red' : 'Odd'
		const rightId = dozenIndex === 0 ? 'Even' : dozenIndex === 1 ? 'Black' : '19_to_36'
		const centerId = `${startNumber}_to_${endNumber}`

		const leftHighlighted = hoveredPlace === leftId
		const rightHighlighted = hoveredPlace === rightId
		const centerHighlighted = hoveredPlace === centerId
		const leftTone = dozenIndex === 1 ? (leftHighlighted ? 'bg-[#fa617b]' : 'bg-[#fe2247]') : leftHighlighted ? 'bg-[#709cb9]' : 'bg-[#2f4553]'

		return (
			<div className='grid grid-cols-2 gap-px text-white sm:gap-0.5 md:gap-1'>
				<BoardCell
					place={centerId}
					className={cn('col-span-2 aspect-[6/1] text-[9px] md:aspect-[4/1] md:text-sm', centerHighlighted ? 'bg-[#709cb9]' : 'bg-[#2f4553]')}
					disabled={isDisabled}
					pendingChips={pendingChips}
					onHoverPlace={onHoverPlace}
					onPlaceBet={onPlaceBet}
				>
					{startNumber} to {endNumber}
				</BoardCell>
				<BoardCell
					place={leftId}
					className={cn('aspect-[3/1] text-[9px] md:aspect-[2/1] md:text-sm', leftTone)}
					disabled={isDisabled}
					pendingChips={pendingChips}
					onHoverPlace={onHoverPlace}
					onPlaceBet={onPlaceBet}
				>
					{leftId.split('_').join(' ')}
				</BoardCell>
				<BoardCell
					place={rightId}
					className={cn('aspect-[3/1] text-[9px] md:aspect-[2/1] md:text-sm', rightHighlighted ? 'bg-[#709cb9]' : 'bg-[#2f4553]')}
					disabled={isDisabled}
					pendingChips={pendingChips}
					onHoverPlace={onHoverPlace}
					onPlaceBet={onPlaceBet}
				>
					{rightId.split('_').join(' ')}
				</BoardCell>
			</div>
		)
	}

	const zeroPlace = '0'
	const zeroHighlighted = hoveredPlace === zeroPlace || (isResultVisible && winningNumber === 0)

	return (
		<div className='flex w-full flex-col gap-1 px-1 pb-1 md:gap-0 md:px-2 md:pb-0'>
			<div className='flex w-full items-end justify-center'>
				<div className='hidden flex-col justify-end pb-2 md:flex'>
					<ActionButton label='Undo' icon={Undo2} disabled={actionsDisabled} onClick={onUndo} iconClassName='h-4 w-4' />
				</div>

				<div className={cn('flex w-full flex-col gap-px sm:gap-0.5 md:w-[80%] md:max-w-3xl md:gap-1 md:pb-2', isDisabled ? 'opacity-70' : 'opacity-100')}>
					<div className='flex items-stretch gap-px sm:gap-0.5 md:gap-1'>
						<div className='w-[7%] shrink-0'>
							<BoardCell
								place={zeroPlace}
								className={cn('aspect-[1/2.5] w-full text-[9px] md:aspect-[1/3] md:text-sm', zeroHighlighted ? 'bg-green-200 text-green-950' : 'bg-green-500')}
								disabled={isDisabled}
								pendingChips={pendingChips}
								onHoverPlace={onHoverPlace}
								onPlaceBet={onPlaceBet}
							>
								0
							</BoardCell>
						</div>

						{[0, 1, 2].map((dozenIndex) => (
							<div key={dozenIndex} className='grid w-[28.4%] min-w-0 grid-cols-4 gap-px sm:gap-0.5 md:gap-1'>
								{Array.from({ length: 12 }).map((_, position) => renderNumberCell(position, dozenIndex))}
							</div>
						))}

						<div className='grid w-[7%] shrink-0 grid-cols-1 gap-px sm:gap-0.5 md:gap-1'>
							{[0, 1, 2].map((columnIndex) => {
								const place = `2:1:${columnIndex}`
								const highlighted = hoveredPlace === place
								return (
									<BoardCell
										key={place}
										place={place}
										className={cn('aspect-[6/5] border-[#424242] text-[8px] md:aspect-square md:text-xs', highlighted ? 'bg-[#709cb9]' : 'bg-[#00000094]')}
										disabled={isDisabled}
										pendingChips={pendingChips}
										onHoverPlace={onHoverPlace}
										onPlaceBet={onPlaceBet}
									>
										2:1
									</BoardCell>
								)
							})}
						</div>
					</div>

					<div className='flex gap-px sm:gap-0.5 md:gap-1'>
						<div className='w-[7%] shrink-0' />
						{[0, 1, 2].map((dozenIndex) => (
							<div key={dozenIndex} className='w-[28.4%] min-w-0'>
								{renderOutsideBets(dozenIndex)}
							</div>
						))}
						<div className='w-[7%] shrink-0' />
					</div>
				</div>

				<div className='hidden flex-col justify-end pb-2 md:flex'>
					<ActionButton label='Clear' icon={Trash2} disabled={actionsDisabled} onClick={onClear} iconClassName='h-4 w-4' />
				</div>
			</div>

			<div className='flex items-center justify-between md:hidden'>
				<ActionButton label='Undo' icon={Undo2} disabled={actionsDisabled} onClick={onUndo} iconClassName='h-2.5 w-2.5' />
				<ActionButton label='Clear' icon={Trash2} disabled={actionsDisabled} onClick={onClear} iconClassName='h-2.5 w-2.5' />
			</div>
		</div>
	)
}
